import {
  Avatar,
  Box,
  Button,
  Flex,
  Text,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Tooltip,
  SimpleGrid,
  Skeleton,
  Spinner,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import {
  MdAddTask,
  MdAttachMoney,
  MdBarChart,
  MdCampaign,
  MdInfo,
} from "react-icons/md";
import ComplexTable from "views/admin/default/components/ComplexTable";
import { columnsDataComplex } from "views/admin/default/variables/columnsData";
import { DateRangePicker } from "react-date-range";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import LineChart from "../../../components/charts/LineChart";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { FetchOverviewReport, UpdateOverviewDateRanges } from "./action";
import { RootReducer } from "../../../RootReducer";
import { Link, useHistory } from "react-router-dom";
import { CheckIcon } from "@chakra-ui/icons";
import {
  FetchBalance,
  RedeemCouponAction,
} from "../../../listener/signin/action";
import { getChartDatesFromRange, NumberWithCommas } from "../../../utils";
import { useOutsideClick } from "hooks/useOutsideClick";
import GamelightButton from "components/button";
import { CSVLink } from "react-csv";
import { columnsDataComplex as columns } from "./variables/csvColumns";
import useNotifications from "store/notificationStore";

const headers = columns.map((col) => ({
  label: col.Header,
  key: col.accessor,
}));

export default function Overview() {
  const dispatch = useDispatch();
  const history = useHistory();
  // Chakra Color Mode
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  const reportRequestState = useSelector(
    (state: RootReducer) => state.overview.fetchOverviewRequestState
  );
  const report = useSelector((state: RootReducer) => state.overview.report);

  const hasUsedCoupon = useSelector(
    (state: RootReducer) => state.authentication.hasUsedCoupon
  );
  const couponRedeemRequestState = useSelector(
    (state: RootReducer) => state.authentication.couponRedeemRequestState
  );
  const [couponCode, setCouponCode] = useState("");

  const isReportSuccessful = reportRequestState.isSuccessful;

  const startDate = useSelector(
    (state: RootReducer) => state.overview.datepickerStartDate
  );
  const endDate = useSelector(
    (state: RootReducer) => state.overview.datepickerEndDate
  );

  const overdueInvoiceNotification = useNotifications((state) =>
    state.getNotificationsByScope("overdueInvoice")
  );

  const data = useMemo(
    () =>
      report
        ? [...report.campaigns].map((c) => ({
            ...c,
            biddingType: c.biddingType === "roas_cpi" ? "ROAS" : "CPI",
            fromDate: moment(startDate).format("YYYY-MM-DD"),
            untilDate: moment(endDate).format("YYYY-MM-DD"),
          }))
        : [],
    [endDate, report, startDate]
  );

  const fileName = useMemo(
    () =>
      `Gamelight_UA_${moment(startDate).format("YYYY-MM-DD")}_${moment(
        endDate
      ).format("YYYY-MM-DD")}`,
    []
  );

  const [ranges, setRanges] = useState({
    startDate: startDate,
    endDate: endDate,
    key: "selection",
  });
  const [internalRanges, setInternalRanges] = useState({
    startDate: startDate,
    endDate: endDate,
    key: "selection",
  });

  const couponValue = useSelector(
    (state: RootReducer) => state.authentication.couponAmountRemaining
  );

  useEffect(() => {
    dispatch(FetchBalance());
    dispatch(FetchOverviewReport(ranges.startDate, ranges.endDate, "all"));
  }, [ranges.startDate, ranges.endDate]);

  const [isDatePickerVisible, setDatePickerVisible] = useState(false);

  const handleClickOutside = useCallback(
    (internalRanges: any) => {
      if (isDatePickerVisible) {
        setRanges(internalRanges);
        setDatePickerVisible(false);
      }
    },
    [isDatePickerVisible]
  );

  const ref = useOutsideClick(handleClickOutside, internalRanges);

  const differentDates =
    internalRanges.startDate.toDateString() ==
    internalRanges.endDate.toDateString()
      ? 1
      : 2;
  const formattedDate =
    differentDates === 1
      ? moment(internalRanges.startDate).format("ll")
      : `${moment(internalRanges.startDate).format("ll")} - ${moment(
          internalRanges.endDate
        ).format("ll")}`;

  const allDatesBetween = getChartDatesFromRange(
    report ? report.from : ranges.startDate,
    report ? report.until : ranges.endDate
  );

  let lineChart = {
    chart: {
      toolbar: {
        show: false,
      },
      dropShadow: {
        enabled: false,
        top: 13,
        left: 0,
        blur: 10,
        opacity: 0.1,
        color: "#4318FF",
      },
    },
    colors: ["#4318FF", "#39B8FF"],
    markers: {
      size: 0,
      colors: "white",
      strokeColors: "#7551FF",
      strokeWidth: 3,
      strokeOpacity: 0.9,
      strokeDashArray: 0,
      fillOpacity: 1,
      // @ts-ignore
      discrete: [],
      shape: "circle",
      radius: 2,
      offsetX: 0,
      offsetY: 0,
      showNullDataPoints: true,
    },
    tooltip: {
      theme: "dark",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      type: "line",
      width: 3,
    },
    xaxis: {
      type: "numeric",
      categories: allDatesBetween,
      labels: {
        style: {
          colors: "#A3AED0",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: [
      {
        labels: {
          formatter: function (val: number) {
            return val.toFixed(0);
          },
        },
      },
    ],
    legend: {
      show: true,
    },
    grid: {
      show: true,
      column: {
        color: ["#7551FF", "#39B8FF"],
        opacity: 0.1,
      },
    },
    color: ["#7551FF", "#39B8FF"],
  };

  let lineChartDataTotalSpent = [
    {
      name: "Installs",
      // @ts-ignore
      data: [],
    },
    {
      name: "Spend",
      // @ts-ignore
      data: [],
    },
  ];

  if (isReportSuccessful) {
    allDatesBetween.forEach((_, i) => {
      lineChartDataTotalSpent[0].data.push(report.graphRows[i].installs);
      lineChartDataTotalSpent[1].data.push(report.graphRows[i].spend);
    });
  }

  let tableColumns = columnsDataComplex;

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {overdueInvoiceNotification.length > 0 && (
        <Link to={"/invoices"}>
          <Alert
            style={{ marginBottom: 20 }}
            status={overdueInvoiceNotification[0].type}
            cursor="pointer"
          >
            <AlertIcon />
            <Box>
              <AlertTitle>{overdueInvoiceNotification[0].title}</AlertTitle>
              <AlertDescription>
                {overdueInvoiceNotification[0].description}
              </AlertDescription>
            </Box>
          </Alert>
        </Link>
      )}
      <Flex style={{ marginBottom: 20 }}>
        {couponValue !== null && couponValue > 0 && (
          <Flex
            style={{
              backgroundColor: "white",
              width: 160,
              marginTop: 6,
              height: 40,
              borderColor: "#e7e7e7",
              borderWidth: 1,
              borderRadius: 12,
              flexDirection: "row",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                color: "#4a34ff",
                fontSize: 16,
                fontWeight: "bold",
                textAlign: "center",
                alignSelf: "center",
              }}
            >
              Balance: ${couponValue.toFixed(0)}
            </Text>
            <Tooltip
              placement="right-end"
              label={
                "After your Balance reaches $0 your campaigns will continue running and you will receive an invoice at the end of the month (Net 30)"
              }
            >
              <span
                style={{
                  alignSelf: "center",
                  fontSize: 20,
                  marginLeft: 6,
                  marginTop: 5,
                }}
              >
                <Icon color={"#4a34ff"} as={MdInfo} />
              </span>
            </Tooltip>
          </Flex>
        )}

        {!hasUsedCoupon && !couponValue && (
          <InputGroup
            borderRadius="16px"
            style={{ width: 240, marginTop: 6, backgroundColor: "white" }}
          >
            <Input
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  dispatch(RedeemCouponAction(couponCode));
                }
              }}
              borderRadius="16px"
              fontSize={{
                lg: 15,
                base: 12,
              }}
              placeholder="Enter Credit Coupon"
              value={couponCode}
              onChange={(c) => {
                setCouponCode(c.target.value);
              }}
            />
            <InputRightElement
              _hover={{
                backgroundColor: "rgba(236,233,255,0.43)",
                borderRadius: 40,
              }}
              onClick={() => {
                dispatch(RedeemCouponAction(couponCode));
              }}
              children={
                couponRedeemRequestState.isLoading ? (
                  <Spinner color="#4a34ff" />
                ) : (
                  <CheckIcon color="#4a34ff" />
                )
              }
            />
          </InputGroup>
        )}
        <Flex
          top={{
            base: 32,
            xl: 28,
          }}
          ref={ref}
          style={{ right: 30 }}
          justifyContent="flex-end"
          alignItems="center"
          width="100%"
        >
          <CSVLink data={data} headers={headers} filename={fileName}>
            <GamelightButton
              disabled={
                !reportRequestState.isSuccessful ||
                !report ||
                !report.campaigns ||
                report.campaigns.length === 0
              }
              mr={2}
              size="sm"
            >
              Export to CSV
            </GamelightButton>
          </CSVLink>
          <Input
            borderRadius={16}
            onClick={() => {
              setInternalRanges(ranges);
              setDatePickerVisible(!isDatePickerVisible);
            }}
            style={{
              backgroundColor: "white",
              textAlign: "center",
              width: differentDates * 120 + 40,
            }}
            defaultValue={formattedDate}
            value={formattedDate}
          />
          {isDatePickerVisible && (
            <Box
              style={{
                position: "absolute",
                right: 20,
                zIndex: 200,
                top: 50,
                boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
              }}
            >
              <DateRangePicker
                ranges={[internalRanges]}
                onChange={(newRanges: any) => {
                  setInternalRanges({
                    endDate: newRanges.selection.endDate,
                    key: newRanges.selection.key,
                    startDate: newRanges.selection.startDate,
                  });
                  dispatch(
                    UpdateOverviewDateRanges(
                      newRanges.selection.startDate,
                      newRanges.selection.endDate
                    )
                  );
                }}
              />
            </Box>
          )}
        </Flex>
      </Flex>
      <SimpleGrid
        style={{ marginTop: 60 }}
        columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
        gap="20px"
        mb="20px"
      >
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon w="32px" h="32px" as={MdBarChart} color={brandColor} />
              }
            />
          }
          isLoading={!isReportSuccessful}
          name="Installs"
          value={report ? report.totalInstalls : null}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg={boxBg}
              icon={
                <Icon w="32px" h="32px" as={MdAttachMoney} color={brandColor} />
              }
            />
          }
          isLoading={!isReportSuccessful}
          name="Spend"
          value={
            report ? `$${NumberWithCommas(report.totalSpend.toFixed(2))}` : null
          }
        />
        <MiniStatistics
          startContent={
            <IconBox
              w="56px"
              h="56px"
              bg="linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)"
              icon={<Icon w="28px" h="28px" as={MdCampaign} color="white" />}
            />
          }
          isLoading={!isReportSuccessful}
          name="Campaigns"
          value={report ? `${report.campaigns.length}` : null}
        />
      </SimpleGrid>
      <Box
        borderRadius={16}
        marginBottom={8}
        backgroundColor={"white"}
        height={300}
      >
        {!isReportSuccessful && (
          <Spinner
            color={"#7f35ff"}
            style={{ marginLeft: "48%", marginTop: 130, alignSelf: "center" }}
            size="lg"
          />
        )}
        {isReportSuccessful && (
          <LineChart
            key={lineChart.xaxis.categories.length}
            chartData={lineChartDataTotalSpent}
            chartOptions={lineChart}
          />
        )}
      </Box>
      <ComplexTable
        isLoading={!isReportSuccessful}
        columnsData={tableColumns}
        tableData={report ? report.campaigns : []}
      />
    </Box>
  );
}
